export const navLinks = [
  { to: '/', label: 'Home' },
  { to: '/about', label: 'About' },
  { to: '/board-experience', label: 'Board Experience' },
  { to: '/expertise', label: 'Expertise' },
  { to: '/experience', label: 'Experience' },
  { to: '/results', label: 'Impact' },
  { to: '/contact', label: 'Contact' },
]

export const profile = {
  name: 'Kevin L. Brandenburg',
  shortName: 'Kevin Brandenburg',
  title: 'Executive Vice President, Chief Procurement Officer & Head of Global Corporate Services',
  subheadline:
    'A global executive and strategic advisor with 39+ years leading enterprise procurement, corporate real estate, workplace strategy, and corporate services across complex multinational organizations.',
  portraitUrl: 'https://cdn.magicpatterns.com/uploads/4nNMvLxhXvHUvYzMwLRaPn/kevin.jpg',
  location: 'Elizabethtown, Kentucky, United States',
  phone: '(937) 287-0680',
  phoneHref: 'tel:+19372870680',
  email: 'kevinleebrandenburg@gmail.com',
  linkedinLabel: 'www.linkedin.com/in/kevin-brandenburg-571132187',
  linkedinUrl: 'https://www.linkedin.com/in/kevin-brandenburg-571132187',
  closing:
    "Let's discuss how my experience can benefit your board or leadership team.",
  valueStatement:
    'An experienced operator’s perspective for leadership teams — commercial judgment combined with deep expertise in procurement, real estate, corporate services, risk, sustainability, and enterprise-scale transformation.',
  positioning:
    'Global portfolios, significant operating budgets, strategic sourcing, M&A activity, risk management, and enterprise transformation — governed at the scale where decisions reach the board.',
}

export const metrics = [
  { prefix: '$', value: 1, suffix: 'B+', decimals: 0, label: 'Annual Procurement Spend Led' },
  { prefix: '', value: 18, suffix: 'M+', decimals: 0, label: 'Sq Ft Real Estate Portfolio Governed' },
  { prefix: '$', value: 220, suffix: 'M+', decimals: 0, label: 'Annual Cost Reductions Delivered' },
  { prefix: '', value: 39, suffix: '+', decimals: 0, label: 'Years of Global Leadership' },
]

export const secondaryMetrics = [
  { value: '21,000+', label: 'Employees supported at Parexel' },
  { value: '45+', label: 'Countries of active operations' },
  { value: '140+', label: 'Global sites overseen at Syneos Health' },
  { value: '$520M+', label: 'Annual operating budget at MetLife' },
]

export const enterprises = [
  { name: 'Parexel', detail: 'EVP, CPO & Global Corporate Services' },
  { name: 'Syneos Health', detail: 'EVP, Corporate Real Estate & Procurement' },
  { name: 'MetLife', detail: 'VP, Global Corporate Real Estate & Services' },
  { name: 'Kendle International', detail: 'Senior Leadership' },
  { name: 'Green Cabbage', detail: 'Executive Advisory Board' },
]

export const aboutParagraphs = [
  'Kevin L. Brandenburg is a seasoned global executive and strategic advisor with more than 39 years of experience leading enterprise procurement, corporate real estate, workplace strategy, and corporate services across complex multinational organizations. His career spans senior leadership roles with Parexel, Syneos Health, MetLife, Kendle International, and other major organizations, with responsibility for global portfolios, significant operating budgets, strategic sourcing, M&A activity, risk management, and enterprise transformation.',
  'Most recently, Kevin served as Executive Vice President, Chief Procurement Officer and Head of Global Corporate Services at Parexel, supporting more than 21,000 employees across 45+ countries. He led global procurement exceeding $900 million in annual spend, a 1.2-million-square-foot international real estate portfolio, global travel, business continuity, sustainability, and corporate services.',
  'Previously, as Executive Vice President and Head of Corporate Real Estate and Global Procurement at Syneos Health, he oversaw 140+ sites across 45 countries and more than 2.1 million square feet of real estate, alongside procurement spend exceeding $1 billion annually. At MetLife, as Vice President and Head of Global Corporate Real Estate & Services, he governed an international portfolio exceeding 18 million square feet and an annual operating budget of more than $520 million.',
  'Today, Kevin serves on the Executive Advisory Board of Green Cabbage, providing strategic counsel on growth, procurement strategy, market development, partnerships, client relationships, and emerging opportunities.',
]

export const governanceAreas = [
  {
    title: 'Enterprise Procurement',
    description:
      'Global sourcing and supplier strategy at scale — $900M+ annual spend at Parexel and procurement exceeding $1B annually at Syneos Health.',
  },
  {
    title: 'Corporate Real Estate & Workplace',
    description:
      'International portfolios from 1.2M sq ft to more than 18M sq ft, including site strategy, portfolio optimization, and workplace strategy.',
  },
  {
    title: 'M&A and Enterprise Transformation',
    description:
      'Senior advisor on global M&A, real estate investments, labor arbitrage, and major acquisitions and dispositions across international markets.',
  },
  {
    title: 'Risk, Continuity & Sustainability',
    description:
      'Global travel, business continuity, sustainability, and corporate services governed as one accountable operating function.',
  },
]

export const boardRole = {
  company: 'Green Cabbage',
  logoUrl:
    'https://cdn.magicpatterns.com/patterns/generated-images/98a1fd13-7dce-4c3b-8424-c1ffc9084a60.jpg',
  role: 'Executive Advisory Board',
  period: 'Present',
  value:
    'Providing strategic counsel on growth, procurement strategy, market development, partnerships, client relationships, and emerging opportunities.',
  focus: [
    {
      title: 'Growth & Market Development',
      detail:
        'Advising leadership on growth strategy, market development, and emerging opportunities, informed by four decades inside enterprise buying organizations.',
    },
    {
      title: 'Procurement Strategy',
      detail:
        'Shaping procurement strategy and commercial positioning with an operator’s view of how enterprise buyers evaluate, fund, and renew services.',
    },
    {
      title: 'Partnerships & Client Relationships',
      detail:
        'Counsel on partnership structure and client relationship strategy, bringing senior-level credibility to leadership and client conversations.',
    },
  ],
  priorBoard: {
    company: 'Edison Supply and Distribution',
    role: 'Board of Directors',
    detail:
      'Prior board service supporting governance, commercial strategy, and operational oversight.',
  },
}

export const boardReadiness = [
  'Enterprise procurement and supplier governance',
  'Global corporate real estate and portfolio strategy',
  'M&A diligence, integration, and synergy realization',
  'Risk management and business continuity oversight',
  'Sustainability and corporate services governance',
  'Cost structure, labor arbitrage, and margin improvement',
]

export const expertise = [
  {
    icon: 'handshake' as const,
    title: 'Enterprise Procurement & Strategic Sourcing',
    description:
      'Global procurement leadership exceeding $900M in annual spend at Parexel and more than $1B annually at Syneos Health, with $30M+ in annual supplier savings delivered.',
    points: ['$1B+ annual spend led', 'Strategic sourcing', 'Supplier savings programs', 'Category governance'],
  },
  {
    icon: 'building' as const,
    title: 'Corporate Real Estate & Workplace Strategy',
    description:
      'International portfolios spanning 1.2M to 18M+ sq ft across 140+ sites and 45 countries, including portfolio optimization and workplace strategy.',
    points: ['18M+ sq ft governed', '140+ global sites', 'Portfolio optimization', 'Workplace strategy'],
  },
  {
    icon: 'trending' as const,
    title: 'M&A, Synergy & Enterprise Transformation',
    description:
      'Senior advisor on global M&A, real estate investments, labor arbitrage, and enterprise transactions, including the post-merger non-labor synergy program at Syneos Health.',
    points: ['Global M&A advisory', 'Synergy realization', 'Acquisitions & dispositions', 'Labor arbitrage'],
  },
  {
    icon: 'shield' as const,
    title: 'Risk, Continuity & Sustainability',
    description:
      'Global travel, business continuity, sustainability, and corporate services owned end to end across 45+ countries of active operations.',
    points: ['Business continuity', 'Global travel program', 'Sustainability', 'Corporate services'],
  },
]

export const caseStudies = [
  {
    id: 'metlife-portfolio',
    company: 'MetLife',
    headline: 'Governing an 18M+ sq ft international portfolio',
    metric: '$110M',
    metricLabel: 'estimated annual real estate and human-capital cost reduction',
    challenge:
      'An international real estate portfolio exceeding 18 million square feet carried an annual operating budget of more than $520 million across multiple international markets.',
    action:
      'Served as senior advisor on strategic planning, global M&A, real estate investments, labor arbitrage, portfolio optimization, and major enterprise transactions including acquisitions and dispositions.',
    outcome:
      'Work contributed to an estimated $110 million in annual real estate and human-capital cost reduction.',
  },
  {
    id: 'syneos-synergy',
    company: 'Syneos Health',
    headline: 'Leading non-labor synergy after a landmark merger',
    metric: '$40M+',
    metricLabel: 'in operating reductions following the 2017 merger',
    challenge:
      'The 2017 merger of INC Research and InVentiv Health created overlapping real estate and procurement structures across 140+ sites in 45 countries.',
    action:
      'Led the company’s non-labor synergy program alongside a 2.1M+ sq ft portfolio and procurement spend exceeding $1 billion annually, identifying strategies across both functions.',
    outcome:
      'Exceeded the original three-year savings target and delivered more than $40 million in operating reductions.',
  },
  {
    id: 'parexel-cost',
    company: 'Parexel',
    headline: 'Running procurement and corporate services at enterprise scale',
    metric: '$70M+',
    metricLabel: 'in combined annual real estate and supplier savings',
    challenge:
      'More than 21,000 employees across 45+ countries depended on procurement, a 1.2M sq ft international portfolio, global travel, continuity, and corporate services.',
    action:
      'Led global procurement exceeding $900 million in annual spend together with real estate, travel, business continuity, sustainability, and corporate services as one accountable function.',
    outcome:
      'Contributed more than $40 million in annual real estate operating-cost reductions and more than $30 million in annual supplier savings while supporting major enterprise initiatives and M&A activity.',
  },
]

export const approachSteps = [
  {
    step: '01',
    title: 'Read the portfolio as a P&L',
    description:
      'Spend, leases, headcount, and site data first. Every recommendation carries the operating-cost math behind it before it reaches a leadership team.',
  },
  {
    step: '02',
    title: 'Consolidate and govern',
    description:
      'Strategic sourcing, portfolio optimization, and risk controls installed so savings are structural rather than one-time — and hold through audit and integration.',
  },
  {
    step: '03',
    title: 'Advise through the transaction',
    description:
      'From diligence to synergy realization, staying with the work through M&A, dispositions, and transformation until the numbers are actually banked.',
  },
]

export const recognition = [
  {
    title: 'Top 15 CPOs in Diversity',
    issuer: 'Diversity Magazine',
    year: '2023',
    detail:
      'Recognized among the leading chief procurement officers advancing diversity within enterprise procurement.',
  },
  {
    title: 'Master of Corporate Real Estate (MCR)',
    issuer: 'CoreNet Global',
    year: 'Credential',
    detail:
      'The corporate real estate profession’s senior designation, covering portfolio strategy, workplace, and transaction management.',
  },
  {
    title: 'BA, Organizational Leadership',
    issuer: 'Northern Kentucky University',
    year: 'Summa cum laude',
    detail:
      'Graduated summa cum laude with a Bachelor of Arts in Organizational Leadership.',
  },
]

export const engagementModels = [
  {
    title: 'Board & Advisory Service',
    description:
      'Independent, advisory, or executive advisory board service bringing procurement, real estate, and transformation fluency to strategic decisions.',
    points: ['Board or advisory seat', 'Quarterly cadence', 'Governance & risk lens'],
  },
  {
    title: 'Executive Advisory Retainer',
    description:
      'Ongoing counsel to a CEO, CFO, or COO on cost structure, supplier strategy, portfolio decisions, and continuity posture.',
    points: ['Monthly retainer', 'Direct executive access', 'Commercial strategy reviews'],
  },
  {
    title: 'Targeted Consulting',
    description:
      'Defined-scope engagements: spend diagnostics, portfolio optimization, M&A synergy programs, or post-merger integration of corporate functions.',
    points: ['Fixed scope & timeline', 'Diagnostic to roadmap', 'Interim leadership available'],
  },
]

export const credentials = [
  {
    label: 'Executive Leadership',
    items: [
      'EVP, Chief Procurement Officer & Head of Global Corporate Services, Parexel',
      'EVP, Head of Corporate Real Estate & Global Procurement, Syneos Health',
      'VP, Head of Global Corporate Real Estate & Services, MetLife',
    ],
  },
  {
    label: 'Board & Advisory',
    items: [
      'Executive Advisory Board, Green Cabbage',
      'Board of Directors, Edison Supply and Distribution',
      'Senior advisor on global M&A and enterprise transactions',
    ],
  },
  {
    label: 'Recognition & Education',
    items: [
      'Top 15 CPOs in Diversity — Diversity Magazine, 2023',
      'Master of Corporate Real Estate (MCR), CoreNet Global',
      'BA Organizational Leadership, summa cum laude, NKU',
    ],
  },
]

export const skillGroups = [
  {
    category: 'Strategy',
    skills: ['Strategic Planning', 'Portfolio Optimization', 'Market Development', 'Team Building'],
  },
  {
    category: 'Operations',
    skills: [
      'Strategic Sourcing',
      'Corporate Real Estate',
      'Workplace Strategy',
      'Supplier Relationship Management',
      'Contract Negotiation',
      'Budgeting',
    ],
  },
  {
    category: 'Governance',
    skills: ['M&A Integration', 'Risk Management', 'Business Continuity', 'Sustainability'],
  },
]

export const careerTimeline = [
  {
    id: 'green-cabbage',
    company: 'Green Cabbage',
    role: 'Executive Advisory Board',
    period: 'Present',
    location: 'United States / Global',
    summary:
      'Strategic counsel on growth, procurement strategy, market development, partnerships, client relationships, and emerging opportunities.',
    achievements: [
      'Advises leadership on procurement strategy and commercial positioning from an enterprise buyer’s perspective.',
      'Guides growth strategy, market development, and evaluation of emerging opportunities.',
      'Supports partnership structure and senior client relationship strategy.',
    ],
  },
  {
    id: 'parexel',
    company: 'Parexel',
    role: 'EVP, Chief Procurement Officer & Head of Global Corporate Services',
    period: 'Most recent',
    location: 'Global',
    summary:
      'Supported more than 21,000 employees across 45+ countries, leading global procurement, a 1.2M sq ft international real estate portfolio, global travel, business continuity, sustainability, and corporate services.',
    achievements: [
      'Led global procurement exceeding $900 million in annual spend.',
      'Contributed more than $40 million in annual real estate operating-cost reductions.',
      'Delivered more than $30 million in annual supplier savings while supporting major enterprise initiatives and M&A activity.',
    ],
  },
  {
    id: 'syneos',
    company: 'Syneos Health',
    role: 'EVP, Head of Corporate Real Estate and Global Procurement',
    period: 'Previously',
    location: '45 countries',
    summary:
      'Oversaw 140+ sites across 45 countries and more than 2.1 million square feet of real estate, with procurement spend exceeding $1 billion annually.',
    achievements: [
      'Led the non-labor synergy program following the 2017 merger of INC Research and InVentiv Health.',
      'Identified strategies that exceeded the original three-year savings target.',
      'Delivered more than $40 million in operating reductions.',
    ],
  },
  {
    id: 'metlife',
    company: 'MetLife',
    role: 'VP, Head of Global Corporate Real Estate & Services',
    period: 'Previously',
    location: 'International',
    summary:
      'Oversaw an international portfolio exceeding 18 million square feet and an annual operating budget of more than $520 million.',
    achievements: [
      'Senior advisor on strategic planning, global M&A, real estate investments, and labor arbitrage.',
      'Led portfolio optimization and major enterprise transactions, including acquisitions and dispositions across multiple international markets.',
      'Contributed to an estimated $110 million in annual real estate and human-capital cost reduction.',
    ],
  },
  {
    id: 'kendle',
    company: 'Kendle International',
    role: 'Senior Leadership, Corporate Real Estate & Procurement',
    period: 'Earlier career',
    location: 'United States / Global',
    summary:
      'Senior leadership responsibility across corporate real estate, procurement, and corporate services in a growing multinational organization.',
    achievements: [
      'Built the procurement and real estate disciplines carried into later enterprise roles.',
      'Supported multi-site operations through periods of organizational growth.',
      'Established supplier and facility standards across regions.',
    ],
  },
  {
    id: 'edison',
    company: 'Edison Supply and Distribution',
    role: 'Board of Directors',
    period: 'Prior board service',
    location: 'United States',
    summary:
      'Board service supporting governance, commercial strategy, and operational oversight.',
    achievements: [
      'Contributed governance oversight and commercial judgment at board level.',
      'Advised on operational strategy from a procurement and distribution perspective.',
      'Grounded later advisory board work in direct directorship experience.',
    ],
  },
]

export const inquirySubjects = [
  'Board Opportunity',
  'Executive Consultation',
  'Advisory Engagement',
  'Speaking Request',
  'Other',
]
